import { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { createPortal } from 'react-dom';
import { AsYouType } from 'libphonenumber-js/mobile';
import { countryFromE164, applyPhoneInput, formatE164ForDisplay } from '../../../utils/phoneNumber';
// Phone and CaretDown removed - simplified to just dial code pill
import { COLORS, TYPOGRAPHY, SHADOWS } from '../../../styles/UISystem';
import { PRIORITY_COUNTRIES, OTHER_COUNTRIES } from './countryData';

/**
 * PhoneInputField Component
 *
 * International phone input with country selector overlay.
 * Uses libphonenumber-js for formatting and outputs E.164 format.
 *
 * Features:
 * - Dial code pill (tappable to open country selector)
 * - Centered modal overlay for country selection (reliable on mobile)
 * - 42 priority countries, divider, then remaining countries
 * - Per-country formatting via AsYouType
 * - E.164 output for storage
 * - forwardRef support for external focus control
 */
export const PhoneInputField = forwardRef(
  (
    {
      value = '', // E.164 format or empty string
      onChange, // Called with E.164 format
      onFocus, // Called when input gains focus
      onBlur, // Called when input loses focus
      onKeyDown, // Called on keydown (for autofill detection)
      placeholder = 'Phone',
      disabled = false,
      autoFocus = false,
      autoComplete = 'tel', // For browser autofill
      name = 'phone', // For browser autofill
      style = {},
      inputStyle = {}, // Reaches the <input>; `style` styles the container
      hideDialCode = false, // Hide the dial code pill (for compact/preview mode)
      embedded = false, // When true, removes internal padding for use inside ContactField
      fontSize = null, // Override font size (e.g., 17 for ContactField, default 20 from TYPOGRAPHY.BODY_REGULAR)
    },
    ref
  ) => {
    // Parse initial country from value or default to US
    // Parsing lives in utils/phoneNumber so it can be tested — this component
    // is overlay and layout around three decisions, and it sits on the phone
    // OTP login path.
    const getInitialCountry = () => countryFromE164(value);

    const [selectedCountry, setSelectedCountry] = useState(getInitialCountry);
    const [displayValue, setDisplayValue] = useState('');
    const [isOverlayOpen, setIsOverlayOpen] = useState(false);

    const inputRef = useRef(null);
    const containerRef = useRef(null);

    // Expose focus method via ref
    useImperativeHandle(ref, () => ({
      focus: () => {
        inputRef.current?.focus();
      },
      blur: () => {
        inputRef.current?.blur();
      },
    }));

    // Format value for display - handles E.164 format
    useEffect(() => {
      // Skip if user is actively typing (displayValue already set by handleInputChange)
      // Only sync when value prop changes externally
      if (!value) {
        setDisplayValue('');
        return;
      }

      // Handle E.164 format (+19176502256)
      if (value.startsWith('+')) {
        try {
          const formatted = formatE164ForDisplay(value, selectedCountry.code);

          // Only update if different to avoid cursor issues
          if (formatted !== displayValue) {
            setDisplayValue(formatted);
          }

          // Follow the country the value belongs to. countryFromE164 returns
          // the default when it can't place the number, so this never clears a
          // selection to nothing.
          const country = countryFromE164(value);
          if (country && country.code !== selectedCountry.code) {
            setSelectedCountry(country);
          }
        } catch (e) {
          // If parsing fails, just show the value without the country code
          const dialCode = selectedCountry.dialCode;
          if (value.startsWith(dialCode)) {
            const newDisplay = value.slice(dialCode.length);
            if (newDisplay !== displayValue) {
              setDisplayValue(newDisplay);
            }
          }
        }
      } else {
        // Non-E.164 value (legacy data stored as national format like "(917) 650-2256")
        // Display as-is — the dial code pill may be redundant but the number is visible
        if (value !== displayValue) {
          setDisplayValue(value);
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]); // Re-run when value prop changes

    // Handle input changes
    const handleInputChange = (e) => {
      const input = e.target.value;

      const { display, e164 } = applyPhoneInput(input, selectedCountry);
      setDisplayValue(display);
      onChange?.(e164);
    };

    // Handle country selection
    const handleCountrySelect = (country) => {
      setSelectedCountry(country);
      setIsOverlayOpen(false);

      // Reformat existing input with new country
      if (displayValue) {
        const digitsOnly = displayValue.replace(/\D/g, '');
        const formatter = new AsYouType(country.code);
        const formatted = formatter.input(digitsOnly);
        setDisplayValue(formatted);

        // Update E.164 output
        const phoneNumber = formatter.getNumber();
        if (phoneNumber) {
          onChange?.(phoneNumber.format('E.164'));
        } else if (digitsOnly) {
          onChange?.(`${country.dialCode}${digitsOnly}`);
        }
      }

      // Refocus input after selection
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    };

    // Open country selector overlay
    const handleOpenCountrySelector = () => {
      if (disabled) return;
      inputRef.current?.blur(); // Dismiss keyboard so overlay is fully visible
      setIsOverlayOpen(true);
    };

    // Close overlay
    const handleCloseOverlay = () => {
      setIsOverlayOpen(false);
    };

    // Auto-focus
    useEffect(() => {
      if (autoFocus && inputRef.current) {
        setTimeout(() => {
          inputRef.current?.focus();
        }, 100);
      }
    }, [autoFocus]);

    // Bottom sheet overlay for country selection
    // Works with keyboard - positions above it rather than fighting it
    const overlayContent = isOverlayOpen && (
      <>
        {/* Semi-transparent backdrop */}
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
            zIndex: 9999,
          }}
          onClick={(e) => {
            e.stopPropagation();
            handleCloseOverlay();
          }}
        />
        {/* Bottom sheet */}
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            maxHeight: '50vh',
            backgroundColor: 'white',
            borderTopLeftRadius: '16px',
            borderTopRightRadius: '16px',
            boxShadow: SHADOWS.TIER3,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            zIndex: 10000,
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: '16px 20px',
              borderBottom: '1px solid #E5E5E5',
              textAlign: 'center',
            }}
          >
            <span
              style={{
                fontSize: '17px',
                fontWeight: '600',
                color: COLORS.DARK_GRAY,
              }}
            >
              Select Country
            </span>
          </div>

          {/* Scrollable country list */}
          <div
            style={{
              flex: 1,
              overflowY: 'auto',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            {/* Priority countries */}
            {PRIORITY_COUNTRIES.map((country) => (
              <CountryRow
                key={country.code}
                country={country}
                isSelected={country.code === selectedCountry.code}
                onSelect={handleCountrySelect}
              />
            ))}

            {/* Divider */}
            <div
              style={{
                height: '1px',
                backgroundColor: '#E5E5E5',
                margin: '8px 16px',
              }}
            />

            {/* Other countries */}
            {OTHER_COUNTRIES.map((country) => (
              <CountryRow
                key={country.code}
                country={country}
                isSelected={country.code === selectedCountry.code}
                onSelect={handleCountrySelect}
              />
            ))}
          </div>
        </div>
      </>
    );

    return (
      <>
        {/* Wrapper for absolute positioning - pill is sibling to input container
            so tapping pill won't trigger iOS auto-focus on the input */}
        <div
          style={{
            position: 'relative',
            ...(embedded ? { flex: 1, minWidth: 0 } : { width: '100%' }),
          }}
        >
          {/* Dial code pill - positioned absolutely OUTSIDE input container */}
          {!hideDialCode && (
            <div
              onClick={disabled ? undefined : handleOpenCountrySelector}
              onMouseDown={(e) => e.preventDefault()}
              onTouchStart={(e) => e.preventDefault()}
              style={{
                position: 'absolute',
                left: embedded ? 0 : '16px', // No offset when embedded in ContactField
                top: '50%',
                transform: embedded ? 'translateY(calc(-50% - 1px))' : 'translateY(-50%)', // Nudge up in embedded to align baselines
                zIndex: 1,
                cursor: disabled ? 'default' : 'pointer',
                padding: '6px 10px',
                borderRadius: '8px',
                backgroundColor: disabled ? 'transparent' : '#F0F0F2',
              }}
            >
              <span
                style={{
                  fontSize: '16px',
                  fontWeight: 500,
                  color: COLORS.MEDIUM_GRAY,
                }}
              >
                {selectedCountry.dialCode}
              </span>
            </div>
          )}

          {/* Input container */}
          <div
            ref={containerRef}
            style={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              padding: embedded ? 0 : '12px 16px',
              // Dynamic left padding based on dial code length
              // Embedded mode: minimal padding just for the pill
              // Standalone mode: Base 48px + ~10px per character handles codes from +1 to +1684
              paddingLeft: hideDialCode
                ? embedded
                  ? 0
                  : '16px'
                : embedded
                  ? `${28 + selectedCountry.dialCode.length * 9}px` // Pill width + gap for cursor
                  : `${Math.max(60, 32 + selectedCountry.dialCode.length * 11)}px`,
              backgroundColor: embedded ? 'transparent' : disabled ? COLORS.DISABLED_GRAY : 'white',
              border: embedded ? 'none' : `1px solid ${COLORS.MEDIUM_DARK_GRAY}`,
              borderRadius: embedded ? 0 : '12px',
              boxSizing: 'border-box',
              opacity: 1,
              ...style,
            }}
          >
            {/* Input field */}
            <input
              ref={inputRef}
              type="tel"
              inputMode="tel"
              name={name}
              autoComplete={autoComplete}
              value={displayValue}
              onChange={handleInputChange}
              onFocus={onFocus}
              onBlur={onBlur}
              onKeyDown={onKeyDown}
              placeholder={placeholder}
              disabled={disabled}
              autoFocus={autoFocus}
              style={{
                // Use TYPOGRAPHY.BODY_REGULAR (20px) for input mode, 17px for display mode (matches other ContactField types)
                ...(disabled
                  ? { fontSize: '17px' }
                  : fontSize
                    ? { fontSize: `${fontSize}px` }
                    : TYPOGRAPHY.BODY_REGULAR),
                flex: 1,
                border: 'none',
                outline: 'none',
                backgroundColor: 'transparent',
                minWidth: 0,
                opacity: 1,
                ...inputStyle,
                color: disabled ? COLORS.MEDIUM_GRAY : COLORS.DARK_GRAY,
                ...(disabled ? { WebkitTextFillColor: COLORS.MEDIUM_GRAY } : {}),
              }}
            />
          </div>
        </div>

        {/* Country selector overlay rendered via portal */}
        {createPortal(overlayContent, document.body)}
      </>
    );
  }
);

// Display name for React DevTools
PhoneInputField.displayName = 'PhoneInputField';

/**
 * CountryRow - Single country option in dropdown
 */
const CountryRow = ({ country, isSelected, onSelect }) => (
  <div
    onClick={() => onSelect(country)}
    onMouseDown={(e) => e.preventDefault()}
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '12px 16px',
      cursor: 'pointer',
      backgroundColor: isSelected ? '#f0f0f0' : 'white',
    }}
  >
    <span
      style={{
        fontSize: '16px',
        color: COLORS.DARK_GRAY,
        fontWeight: isSelected ? 500 : 400,
      }}
    >
      {country.name}
    </span>
    <span
      style={{
        fontSize: '16px',
        color: COLORS.MEDIUM_GRAY,
      }}
    >
      {country.dialCode}
    </span>
  </div>
);

/**
 * PhoneInputFieldExample - For UI Showcase
 */
export const PhoneInputFieldExample = () => {
  const [phone1, setPhone1] = useState('');
  const [phone2, setPhone2] = useState('+41791234567');
  const [phone3, setPhone3] = useState('');

  return (
    <div style={{ padding: '20px', maxWidth: '400px', margin: '0 auto' }}>
      <h2 style={{ color: COLORS.DARK_GRAY, marginBottom: '24px', textAlign: 'center' }}>
        PhoneInputField
      </h2>

      {/* Default (US) */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ fontSize: '14px', color: COLORS.MEDIUM_GRAY, marginBottom: '8px' }}>
          Default (US)
        </div>
        <PhoneInputField value={phone1} onChange={setPhone1} />
        <div style={{ fontSize: '12px', color: COLORS.MEDIUM_GRAY, marginTop: '4px' }}>
          E.164: {phone1 || '(empty)'}
        </div>
      </div>

      {/* Pre-filled Swiss number */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ fontSize: '14px', color: COLORS.MEDIUM_GRAY, marginBottom: '8px' }}>
          Pre-filled (Switzerland)
        </div>
        <PhoneInputField value={phone2} onChange={setPhone2} />
        <div style={{ fontSize: '12px', color: COLORS.MEDIUM_GRAY, marginTop: '4px' }}>
          E.164: {phone2 || '(empty)'}
        </div>
      </div>

      {/* Disabled */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ fontSize: '14px', color: COLORS.MEDIUM_GRAY, marginBottom: '8px' }}>
          Disabled
        </div>
        <PhoneInputField value="+19176502256" onChange={() => {}} disabled={true} />
      </div>

      {/* WhatsApp label variant */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ fontSize: '14px', color: COLORS.MEDIUM_GRAY, marginBottom: '8px' }}>
          WhatsApp field
        </div>
        <PhoneInputField value={phone3} onChange={setPhone3} placeholder="WhatsApp" />
        <div style={{ fontSize: '12px', color: COLORS.MEDIUM_GRAY, marginTop: '4px' }}>
          E.164: {phone3 || '(empty)'}
        </div>
      </div>
    </div>
  );
};

export default PhoneInputField;
